import { useTranslation } from 'react-i18next'
import { useState, useRef, useEffect } from 'react'
import './InputAdvanced.css'
import { loadGoogleMapsPlaces } from '@/utils/googleMaps'

declare global {
    interface Window {
        google: any
    }
}

interface FieldProps {
    name: string
    label?: string
    description: string
    hide_label?: boolean
    type?: string
}

interface InputAdvancedProps {
    field: FieldProps
    value?: string
    onInputChange: (field: FieldProps, value: string) => void
    hasError?: boolean
    errorMessage?: string
    startContent?: React.ReactNode
    endContent?: React.ReactNode
    onPlaceSelected?: (place: any) => void
}

const InputAdvanced = ({ field, value, onInputChange, hasError, errorMessage, startContent, endContent, onPlaceSelected }: InputAdvancedProps) => {
    const { t } = useTranslation('common')
    const inputRef = useRef<HTMLInputElement>(null)
    const [internalValue, setInternalValue] = useState(value || '')

    useEffect(() => {
        if (value !== undefined && value !== internalValue) {
            setInternalValue(value)
        }
    }, [value, internalValue])

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const currentValue = e.target.value
        setInternalValue(currentValue)
        onInputChange(field, currentValue)
    }

    const isAddressAutocomplete = field.name === 'googleAddressAutocompleteTrigger'

    useEffect(() => {
        let autocomplete: any = null
        let pacCompleteListener: any = null

        const initAutocomplete = async () => {
            if (!isAddressAutocomplete || !inputRef.current) return

            await loadGoogleMapsPlaces()

            if (!window.google?.maps?.places || !inputRef.current) return

            autocomplete = new window.google.maps.places.Autocomplete(inputRef.current, {
                types: ['address'],
                componentRestrictions: { country: ['pe'] },
            })

            pacCompleteListener = autocomplete.addListener('place_changed', () => {
                const place = autocomplete?.getPlace()
                let addressValue = inputRef.current?.value || ''

                if (place && place.formatted_address) {
                    addressValue = place.formatted_address
                }

                if (inputRef.current) {
                    inputRef.current.value = addressValue
                }
                setInternalValue(addressValue)
                onInputChange(field, addressValue)
                onPlaceSelected?.(place)
            })

            const handleManualInput = (event: Event) => {
                const target = event.target as HTMLInputElement
                if (target.value !== internalValue) {
                    setInternalValue(target.value)
                    onInputChange(field, target.value)
                }
            }

            const currentInput = inputRef.current
            currentInput?.addEventListener('input', handleManualInput)

            return () => {
                currentInput?.removeEventListener('input', handleManualInput)
            }
        }

        const cleanupPromise = initAutocomplete()

        return () => {
            cleanupPromise.then(cleanup => cleanup?.())
            if (pacCompleteListener && window.google?.maps?.event) {
                window.google.maps.event.removeListener(pacCompleteListener)
            }
        }
    }, [field, internalValue, isAddressAutocomplete, onInputChange, onPlaceSelected])

    const inputClassName = `input-advanced-field ${hasError ? 'input-error' : ''}`
    const displayLabel = field.label ? t(field.label) : (field.name && !field.hide_label ? t(field.name) : '')
    const displayPlaceholder = field.description ? t(field.description) : ''

    return (
        <div className="input-advanced-div">
            {!field.hide_label && displayLabel && (
                <div className="input-advanced-label">{displayLabel}</div>
            )}
            <div className="input-advanced-wrapper">
                {startContent && <div className="input-advanced-start">{startContent}</div>}
                <input
                    type={field.type || 'text'}
                    ref={inputRef}
                    className={inputClassName}
                    name={field.name}
                    value={internalValue}
                    onChange={handleChange}
                    placeholder={displayPlaceholder}
                />
                {endContent && <div className="input-advanced-end">{endContent}</div>}
            </div>
            {hasError && errorMessage && (
                <div className="input-advanced-field-message-disclaimer">{errorMessage}</div>
            )}
        </div>
    )
}

export default InputAdvanced

